import { useFlags } from '@atlaskit/flag';
import { useDialog } from 'core/providers/DialogProvider';
import { showErrorFlag, showSuccessFlag } from 'core/utilities/flags-helper';
import { useCallback, useEffect, useState } from 'react';
import { IButton } from 'shared/components/buttons/button-interface';
import SharedDialogBase from 'shared/components/dialog-base/dialog-base';
import SharedForm from 'shared/components/form/form';
import { RunLocationsApiService } from 'core/api';
import { useAuthState } from 'core/providers/AuthProvider';
import { ISharedFormField } from 'shared/components/form/form-interface';
import { AddEditRunLocationsFormFields } from 'core/config/form-fields';
import { IAddEditRunLocationsFormOutput } from '../types';
import { IRunDao, IRunLocationDao, IRunLocationDto } from 'core/api/types/runs.interface';
import { deleteField } from 'firebase/firestore';
import { useSharedData } from 'core/providers/SharedDataProvider';

const AddEditRunLocationsDialog = ({ run, runLocation }: { run: IRunDao; runLocation?: IRunLocationDao }) => {
  const { userData } = useAuthState();
  const flags = useFlags();
  const dialog = useDialog();
  const { processTypes, locations, subLocations } = useSharedData();
  const [formFields, setFormFields] = useState<ISharedFormField[]>(AddEditRunLocationsFormFields);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [selectedRunLocation, setSelectedRunLocation] = useState<string | undefined>(runLocation?.uid);

  const getDefaultValue = useCallback(
    (runLocation: IRunLocationDao, key: string) => {
      switch (key) {
        case 'locationId':
          return {
            value: runLocation.locationId,
            label: locations?.find((location) => location.uid === runLocation.locationId)?.name,
          };
        case 'processTypeIds':
          return runLocation.processTypeIds?.map((processTypeId) => {
            const processType = processTypes?.find((processType) => processType.uid === processTypeId);

            return {
              value: processType?.uid,
              label: processType?.name,
            };
          });
        case 'subLocationId':
          return {
            value: runLocation.subLocationId,
            label: subLocations?.find((subLocation) => subLocation.uid === runLocation.subLocationId)?.name,
          };
        default:
          return runLocation[key as keyof IRunLocationDao];
      }
    },
    [locations, processTypes, subLocations]
  );

  useEffect(() => {
    const fields = AddEditRunLocationsFormFields.map((field) => {
      if (field.key === 'locationId') {
        return {
          ...field,
          options: locations
            ?.filter((location) => location.active)
            ?.sort((a, b) => a.name.localeCompare(b.name))
            .map((location) => ({ value: location.uid, label: location.name })),
          defaultValue: runLocation ? getDefaultValue(runLocation, field.key) : field.defaultValue,
        };
      }

      if (field.key === 'subLocationId') {
        return {
          ...field,
          isDisabled: !selectedRunLocation,
          options: subLocations
            ?.sort((a, b) => a.name.localeCompare(b.name))
            .filter((subLocation) => subLocation.locationId === selectedRunLocation)
            .map((subLocation) => ({ value: subLocation.uid, label: subLocation.name })),
          defaultValue: runLocation ? getDefaultValue(runLocation, field.key) : field.defaultValue,
        };
      }

      if (field.key === 'processTypeIds' && processTypes) {
        return {
          ...field,
          options: processTypes
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((processType) => ({ value: processType.uid, label: processType.name })),
          defaultValue: runLocation ? getDefaultValue(runLocation, field.key) : field.defaultValue,
          isSearchable: true,
        };
      }

      return {
        ...field,
        defaultValue: runLocation ? getDefaultValue(runLocation, field.key) : field.defaultValue,
      };
    });

    setFormFields(fields as ISharedFormField[]);
  }, [runLocation, locations, subLocations, processTypes, selectedRunLocation, getDefaultValue]);

  const handleFormChange = (data: IAddEditRunLocationsFormOutput) => {
    if (data.locationId) {
      setSelectedRunLocation(data.locationId?.value);
    }
  };

  const AddEditRunLocations = async (data: IAddEditRunLocationsFormOutput) => {
    setFormSubmitting(true);
    const payload: IRunLocationDto = {
      runId: run.uid,
      locationId: data.locationId?.value,
      notes: data.notes,
      daysOfWeek: data.daysOfWeek,
      processTypeIds: data.processTypeIds.map((option) => option.value),
    };

    try {
      if (runLocation) {
        await RunLocationsApiService.update(
          runLocation?.uid,
          {
            ...payload,
            scheduledArrivalTime: data.scheduledArrivalTime ?? deleteField(),
            scheduledDepartureTime: data.scheduledDepartureTime ?? deleteField(),
            subLocationId: data.subLocationId?.value ?? deleteField(),
          },
          {
            fullName: userData?.fullName ?? '',
            uid: userData?.uid ?? '',
          }
        );
      } else {
        await RunLocationsApiService.set(
          {
            ...payload,
            ...(data.scheduledArrivalTime && { scheduledArrivalTime: data.scheduledArrivalTime }),
            ...(data.scheduledDepartureTime && { scheduledDepartureTime: data.scheduledDepartureTime }),
            ...(data.subLocationId && { subLocationId: data.subLocationId?.value }),
          },
          {
            fullName: userData?.fullName ?? '',
            uid: userData?.uid ?? '',
          }
        );
      }

      showSuccessFlag(
        runLocation ? 'Run location updated' : 'Run location created',
        `The run location has been ${runLocation ? 'updated' : 'created'} successfully`,
        flags
      );
      dialog?.closeDialog();
    } catch (error) {
      showErrorFlag(
        runLocation ? 'Run location update failed' : 'Run location creation failed',
        `Unable to ${runLocation ? 'update' : 'create'} a run location, please try again.`,
        flags
      );
      setFormSubmitting(false);
    }
  };

  const cancelButton: IButton = {
    onClick: () => dialog?.closeDialog(),
    label: 'Cancel',
    appearance: 'subtle',
    type: 'button',
  };

  const customContent = () => {
    return (
      <>
        <SharedForm
          className='p-4 overflow-y-auto'
          onSubmit={AddEditRunLocations}
          fields={formFields as ISharedFormField[]}
          buttonLabel='Submit'
          loading={formSubmitting}
          cancelButton={cancelButton}
          onChange={handleFormChange}
        />
      </>
    );
  };

  return (
    <SharedDialogBase
      title={`${runLocation ? 'Update' : 'Add'} Run Location`}
      customContentTemplate={customContent()}
      showButtons={false}
    />
  );
};

export default AddEditRunLocationsDialog;
